import React, { useEffect, useMemo, useRef, useState } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { extractTimetableDays, transformToPrayerTimes } from "../../services/api";
import {
  azureStorageService,
  isMissingBlobNotFoundError,
} from "../../services/azureStorageService";
import { timetableDB } from "../../db/indexedDb";
import { runtimeConfig } from "../../config/runtime";
import { masjidSolidColour } from "../../utils/masjidColour";
import { applyPrayerNameTokens } from "../../utils/prayerNames";
import { ScreenData } from "../../types/ScreenTypes";
import BrandMark from "./BrandMark";
import CachedImage from "../shared/CachedImage";
import "./MonthTimetable.css";

interface MonthTimetableProps {
  masjidId: number;
  masjidName: string;
  masjidColor?: string;
  timeZone?: string;
  use12HourFormat?: boolean;
  prayerNames?: Partial<ScreenData["customizedNames"]>;
  jamaahWord?: string;
  onClose?: () => void;
}

type Month = { year: number; month: number };
type MonthResult = PrayerTimes[] | "missing" | "offline";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Months are numbered year * 12 + (month - 1) so the arrows can just add or subtract one.
const monthAt = (index: number): Month => ({
  year: Math.floor(index / 12),
  month: (index % 12) + 1,
});
const indexOf = ({ year, month }: Month) => year * 12 + month - 1;

// Headings are tokens so they show the masjid's own prayer names.
const BEGINS = [
  ["{Fajr}", "BegFajr"],
  ["{Sunrise}", "Sunrise"],
  ["{Dhuhr}", "BegDhuhr"],
  ["{Asar}", "BegAsar"],
  ["{Maghrib}", "BegMaghrib"],
  ["{Isha}", "BegEsha"],
] as const;

const JAMAAH = [
  ["{Fajr}", "JamFajr"],
  ["{Dhuhr}", "JamDhuhr"],
  ["{Asar}", "JamAsar"],
  ["{Maghrib}", "JamMaghrib"],
  ["{Isha}", "JamEsha"],
] as const;

// Maghrib follows sunset and changes almost daily, so it is shown in full like the begin times.
const DAILY_JAMAAH = new Set<string>(["JamMaghrib"]);
const IDLE_CLOSE_MS = 2 * 60 * 1000;

const hasTime = (t?: string) => !!t && t !== "--:--";

// Paper timetables show times without am/pm.
const formatTime = (t: string, use12: boolean) => {
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const mm = String(m).padStart(2, "0");
  return use12 ? `${h % 12 || 12}:${mm}` : `${String(h).padStart(2, "0")}:${mm}`;
};

const todayIn = (timeZone?: string) => {
  const [y, m, d] = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone || "Europe/London",
  })
    .format(new Date())
    .split("-")
    .map(Number);
  return { year: y, month: m, day: d };
};

// Minutes ahead of UTC at midday on that date in the masjid's time zone.
const utcOffsetMinutes = (date: Date, timeZone?: string) => {
  const name =
    new Intl.DateTimeFormat("en-GB", {
      timeZone: timeZone || "Europe/London",
      timeZoneName: "shortOffset",
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value ?? "GMT";
  const match = /GMT([+-])(\d+)(?::(\d+))?/.exec(name);
  if (!match) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3] || 0);
  return match[1] === "-" ? -minutes : minutes;
};

const utcDate = (y: number, m: number, d: number) =>
  new Date(Date.UTC(y, m - 1, d, 12));

const monthLabel = ({ year, month }: Month) =>
  utcDate(year, month, 1).toLocaleDateString("en-GB", {
    month: "long",
    timeZone: "UTC",
  });

const toDays = (timetable: unknown): MonthResult => {
  const days = extractTimetableDays(timetable)
    .map(transformToPrayerTimes)
    .sort((a, b) => Number(a.Date) - Number(b.Date));
  return days.length ? days : "missing";
};

// Online it always checks storage (an ETag check, cheap when unchanged), so a month viewed ahead
// of time never stays stale; offline it falls back to the copy saved on the tablet.
async function loadMonth(masjidId: number, { year, month }: Month): Promise<MonthResult> {
  try {
    const { data, eTag } = await azureStorageService.getPrayerTimesFromBlob(
      masjidId,
      `timetables/${masjidId}/${year}/${month}.json.gz`,
    );
    const raw = extractTimetableDays(data);
    await timetableDB.saveTimetable(String(year), String(month), String(masjidId), {
      data: raw,
      eTag,
    });
    return toDays(raw);
  } catch (error) {
    if (isMissingBlobNotFoundError(error)) return "missing";
    // Also when the Wi-Fi is up but there's no internet behind it.
    const saved = await timetableDB.getTimetable(String(year), String(month), String(masjidId));
    return saved ? toDays(saved) : "offline";
  }
}

function buildMonth(
  days: PrayerTimes[],
  { year, month }: Month,
  today: { year: number; month: number; day: number },
  timeZone: string | undefined,
  use12: boolean,
) {
  // Per jama'ah column: what the row above shows (for ditto marks), and the last ordinary time
  // (for bold changes).
  const lastShown: Record<string, string | undefined> = {};
  const lastReal: Record<string, string | undefined> = {};
  let clockBanner: string | null = null;
  let prevOffset: number | null = null;

  const rows = days.map((d) => {
    const day = Number(d.Date);
    const date = utcDate(year, month, day);
    const offset = utcOffsetMinutes(date, timeZone);
    const clockChange = prevOffset !== null && offset !== prevOffset;
    if (clockChange && !clockBanner) {
      clockBanner = `Clocks go ${offset < (prevOffset ?? offset) ? "back" : "forward"} one hour on ${date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", timeZone: "UTC" })}`;
    }
    prevOffset = offset;

    const friday = date.getUTCDay() === 5;
    const jamaah = JAMAAH.map(([, key]) => {
      const value = d[key];
      const daily = DAILY_JAMAAH.has(key);
      const above = lastShown[key];
      lastShown[key] = hasTime(value) ? value : undefined;
      if (!hasTime(value)) return { text: "", ditto: false, changed: false, daily };
      const usual = lastReal[key];
      // Some masjids put Jumu'ah in Friday's Dhuhr: shown, but not a change, and not what the
      // next ordinary day is compared with.
      const fridayDhuhr = friday && key === "JamDhuhr";
      const jumuah = fridayDhuhr && value !== usual;
      if (!fridayDhuhr) lastReal[key] = value;
      // A ditto only ever means "same as the row above".
      if (!daily && value === above) return { text: "”", ditto: true, changed: false, daily };
      const changed = !daily && !jumuah && usual !== undefined && value !== usual;
      return { text: formatTime(value, use12), ditto: false, changed, daily };
    });

    return {
      day,
      weekday: date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" }),
      friday,
      today: today.year === year && today.month === month && today.day === day,
      clockChange,
      hijri: d.HijriDateComponents?.day ? String(d.HijriDateComponents.day) : "",
      begins: BEGINS.map(([, key]) => (hasTime(d[key]) ? formatTime(d[key], use12) : "")),
      jamaah,
      jumuah: [d.Jummah1, d.Jummah2, d.Jummah3, d.Jummah4]
        .filter((t): t is string => hasTime(t))
        .map((t) => formatTime(t, use12)),
    };
  });

  // Jumu'ah times, with a "from …" line whenever they change during the month.
  const jumuah: string[] = [];
  let lastSet = "";
  rows
    .filter((r) => r.friday && r.jumuah.length > 0)
    .forEach((r) => {
      const set = r.jumuah.join(" · ");
      if (set === lastSet) return;
      const from = utcDate(year, month, r.day).toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      });
      jumuah.push(jumuah.length === 0 ? set : `From ${from}: ${set}`);
      lastSet = set;
    });

  const firstHijri = days[0]?.HijriDateComponents;
  const lastHijri = days[days.length - 1]?.HijriDateComponents;
  const hijriTitle =
    firstHijri?.monthName && lastHijri?.monthName
      ? firstHijri.monthName === lastHijri.monthName
        ? `${firstHijri.monthName} ${lastHijri.year}`
        : `${firstHijri.monthName} – ${lastHijri.monthName} ${lastHijri.year}`
      : "";

  // Columns with no times all month are left out, as on paper.
  const beginCols = BEGINS.map((_, i) => i).filter((i) => rows.some((r) => r.begins[i]));
  const jamaahCols = JAMAAH.map((_, i) => i).filter((i) => rows.some((r) => r.jamaah[i].text));

  return {
    rows,
    jumuah,
    clockBanner,
    hijriTitle,
    beginCols,
    jamaahCols,
    showHijri: rows.some((r) => r.hijri),
  };
}

// The masjid's month as a paper-style timetable: begin and jama'ah times, Fridays shaded,
// today picked out, and jama'ah times shown only on the day they change.
const MonthTimetable: React.FC<MonthTimetableProps> = ({
  masjidId,
  masjidName,
  masjidColor,
  timeZone,
  use12HourFormat = true,
  prayerNames,
  jamaahWord = "Jama'ah",
  onClose,
}) => {
  const today = useMemo(() => todayIn(timeZone), [timeZone]);
  const thisMonth = indexOf(today);
  const first = today.year * 12; // January this year
  const last = (today.year + 1) * 12 + 11; // December next year
  const [selected, setSelected] = useState(thisMonth);
  const [picking, setPicking] = useState(false);
  const [result, setResult] = useState<{ index: number; value: MonthResult }>();
  const todayRow = useRef<HTMLTableRowElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const { colour, ink } = masjidSolidColour(masjidColor);

  useEffect(() => {
    let current = true;
    loadMonth(masjidId, monthAt(selected)).then(
      (value) => current && setResult({ index: selected, value }),
    );
    return () => {
      current = false;
    };
  }, [masjidId, selected]);

  const value = result?.index === selected ? result.value : undefined;
  const view = useMemo(
    () =>
      Array.isArray(value)
        ? buildMonth(value, monthAt(selected), today, timeZone, use12HourFormat)
        : null,
    [value, selected, today, timeZone, use12HourFormat],
  );

  useEffect(() => {
    if (todayRow.current) todayRow.current.scrollIntoView({ block: "center" });
    else scroller.current?.scrollTo({ top: 0 });
  }, [view]);

  useEffect(() => {
    if (!onClose) return;
    let timer = window.setTimeout(onClose, IDLE_CLOSE_MS);
    const restart = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(onClose, IDLE_CLOSE_MS);
    };
    window.addEventListener("pointerdown", restart, true);
    window.addEventListener("scroll", restart, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", restart, true);
      window.removeEventListener("scroll", restart, true);
    };
  }, [onClose]);

  const month = monthAt(selected);
  const title = `${monthLabel(month)} ${month.year}`;

  return (
    <div
      className="month-timetable"
      style={
        {
          "--mt-colour": `var(--home-month, ${colour})`,
          "--mt-ink": `var(--home-month-ink, ${ink})`,
        } as React.CSSProperties
      }
    >
      <header className="mt-header">
        <CachedImage
          src={`${runtimeConfig.storageUrl}/masaajidimages/${masjidId}.png`}
          alt=""
          className="mt-photo"
        />
        <div className="mt-titles">
          <div className="mt-name">{masjidName}</div>
          <button
            className="mt-month"
            onClick={() => setPicking(!picking)}
            aria-expanded={picking}
          >
            {title} <span className="mt-caret">▾</span>
          </button>
          {view?.hijriTitle && <div className="mt-hijri">{view.hijriTitle}</div>}
        </div>
        <nav className="mt-switch">
          <button
            onClick={() => setSelected(selected - 1)}
            disabled={selected <= first}
            aria-label="Previous month"
          >
            ‹
          </button>
          <button
            onClick={() => setSelected(selected + 1)}
            disabled={selected >= last}
            aria-label="Next month"
          >
            ›
          </button>
        </nav>
        {onClose && (
          <button className="mt-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </header>

      {picking && (
        <div className="mt-picker">
          {[today.year, today.year + 1].map((year) => (
            <div key={year}>
              <div className="mt-picker-year">{year}</div>
              <div className="mt-picker-grid">
                {MONTH_NAMES.map((name, i) => {
                  const index = year * 12 + i;
                  return (
                    <button
                      key={name}
                      className={`${index === selected ? "active" : ""} ${index === thisMonth ? "now" : ""}`}
                      onClick={() => {
                        setSelected(index);
                        setPicking(false);
                      }}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {!picking && view?.clockBanner && <div className="mt-banner">{view.clockBanner}</div>}

      <div className="mt-scroll" ref={scroller} hidden={picking}>
        {value === "missing" && (
          <div className="mt-empty">
            {selected > thisMonth ? (
              <>
                {title}'s timetable from {masjidName} isn't out yet.
                <br />
                It will appear here as soon as they publish it.
              </>
            ) : (
              <>There's no {title} timetable from {masjidName} on Masjid 24/7.</>
            )}
          </div>
        )}
        {value === "offline" && (
          <div className="mt-empty">
            {title} can't be loaded right now.
            <br />
            Check the tablet's internet connection.
          </div>
        )}
        {view && (
          <>
            <table className="mt-table">
              <thead>
                <tr className="mt-groups">
                  <th className="blank" colSpan={view.showHijri ? 3 : 2} />
                  <th className="begins" colSpan={view.beginCols.length}>
                    Beginning times
                  </th>
                  {view.jamaahCols.length > 0 && (
                    <th className="jamaah" colSpan={view.jamaahCols.length}>
                      {jamaahWord} times
                    </th>
                  )}
                </tr>
                <tr className="mt-cols">
                  <th>Date</th>
                  <th>Day</th>
                  {view.showHijri && <th>Hijri</th>}
                  {view.beginCols.map((c, n) => (
                    <th key={`b-${c}`} className={n === 0 ? "first" : ""}>
                      {applyPrayerNameTokens(BEGINS[c][0], prayerNames)}
                    </th>
                  ))}
                  {view.jamaahCols.map((c, n) => (
                    <th key={`j-${c}`} className={n === 0 ? "first" : ""}>
                      {applyPrayerNameTokens(JAMAAH[c][0], prayerNames)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.rows.map((r) => (
                  <tr
                    key={r.day}
                    ref={r.today ? todayRow : undefined}
                    className={[
                      r.friday ? "friday" : "",
                      r.today ? "today" : "",
                      r.clockChange ? "clock-change" : "",
                    ].join(" ")}
                  >
                    <td className="date">{r.day}</td>
                    <td className="day">{r.weekday}</td>
                    {view.showHijri && <td className="hijri">{r.hijri}</td>}
                    {view.beginCols.map((c, n) => (
                      <td
                        key={`b${c}`}
                        className={`begin${n === 0 ? " first" : ""}${BEGINS[c][1] === "Sunrise" ? " sunrise" : ""}`}
                      >
                        {r.begins[c]}
                      </td>
                    ))}
                    {view.jamaahCols.map((c, n) => {
                      const j = r.jamaah[c];
                      return (
                        <td
                          key={`j${c}`}
                          className={`jam${n === 0 ? " first" : ""}${j.ditto ? " ditto" : ""}${j.changed ? " changed" : ""}${j.daily ? " daily" : ""}`}
                        >
                          {j.text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {view.jumuah.length > 0 && (
              <div className="mt-jumuah">
                <span className="label">{applyPrayerNameTokens("{Jummah}", prayerNames)}</span>
                {view.jumuah.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </div>
            )}
            <BrandMark className="mt-brand" />
          </>
        )}
      </div>
    </div>
  );
};

export default MonthTimetable;
